import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useVisitorData from "layouts/tables/data/visitorTableData";

function Visitors() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedVisitor,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
  } = useVisitorData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading visitors...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Visitors"
        subtitle="Track first-time and returning visitors, and follow up with them."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              New Visitor
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search visitors..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={form.id ? "Edit Visitor" : "New Visitor"}
        onSave={handleSubmit}
        saveLabel={isSaving ? "Saving..." : form.id ? "Save Changes" : "Add Visitor"}
        saveDisabled={
          !form.first_name.trim() || !form.surname.trim() || !form.visit_date || isSaving
        }
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="First Name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Surname"
              value={form.surname}
              onChange={(e) => setForm({ ...form, surname: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              margin="normal"
            />
          </Box>
          <TextField
            label="Address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            fullWidth
            margin="normal"
          />
        </FormSection>

        <FormSection>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Visit Date"
              type="date"
              value={form.visit_date}
              onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
              fullWidth
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Follow-up Date"
              type="date"
              value={form.follow_up_date}
              onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="How did they hear about us?"
            placeholder="e.g. Invited by a member, social media, walk-in"
            value={form.how_heard}
            onChange={(e) => setForm({ ...form, how_heard: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="contacted">Contacted</MenuItem>
            <MenuItem value="follow_up_needed">Follow-up Needed</MenuItem>
            <MenuItem value="converted">Converted</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField
            label="Follow-up Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={3}
          />
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedVisitor?.firstName} ${selectedVisitor?.surname}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Visitors;
